import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiMail, 
  FiLock, 
  FiUser,
  FiCheck,
  FiAlertCircle
} from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook } from 'react-icons/fa';
import './Connexion.css';
import { authAPI } from '../../api';

const Connexion = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.email.trim()) {
            newErrors.email = 'L\'email est requis';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Format d\'email invalide';
        }
        
        if (!formData.password) {
            newErrors.password = 'Le mot de passe est requis';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        try {
            setLoading(true);
            
            const response = await authAPI.login({
                email: formData.email,
                password: formData.password
            });
            
            if (response.success) {
                localStorage.setItem('token', response.token);
                localStorage.setItem('user', JSON.stringify(response.user));
                localStorage.setItem('userRole', response.user.role || 'user');
                
                if (rememberMe) {
                    localStorage.setItem('rememberedEmail', formData.email);
                } else {
                    localStorage.removeItem('rememberedEmail');
                }
                
                if (response.user.role === 'admin') {
                    navigate('/admin');
                } else {
                    navigate('/');
                    alert(`Bienvenue ${response.user.name} ! Vous êtes maintenant connecté.`);
                }
            } else {
                throw new Error(response.message || 'Erreur de connexion');
            }
        } catch (error) {
            console.error('Erreur de connexion:', error);
            
            if (error.status === 401) {
                setErrors({
                    general: 'Email ou mot de passe incorrect'
                });
            } else if (error.errors) {
                setErrors(error.errors);
            } else {
                setErrors({
                    general: error.message || 'Une erreur est survenue lors de la connexion'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="connexion-container">
            {/* Effets de fond */}
            <div className="background-shapes">
                <div className="shape shape-1"></div>
                <div className="shape shape-2"></div>
                <div className="shape shape-3"></div>
                <div className="shape shape-4"></div>
                <div className="shape shape-5"></div>
            </div>

            {/* Lien de retour */}
            <Link to="/" className="back-to-home">
                <FiArrowLeft className="back-icon" />
                <span>Retour à l'accueil</span>
            </Link>

            <div className="connexion-wrapper">
                {/* Section gauche - Illustrations */}
                <div className="connexion-hero">
                    <div className="hero-content">
                        <div className="hero-icon">
                            <div className="icon-wrapper">
                                <FiUser className="user-icon" />
                            </div>
                        </div>
                        <h1 className="hero-title">Bienvenue sur<br/><span className="highlight">ETEF</span> Connect</h1>
                        <p className="hero-subtitle">
                            Accédez à votre espace personnel pour gérer votre parcours d'études en France
                        </p>
                        <div className="hero-features">
                            <div className="feature-item">
                                <FiCheck className="feature-icon" />
                                <span>Suivi personnalisé</span>
                            </div>
                            <div className="feature-item">
                                <FiCheck className="feature-icon" />
                                <span>Documents sécurisés</span>
                            </div>
                            <div className="feature-item">
                                <FiCheck className="feature-icon" />
                                <span>Support 24/7</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section droite - Formulaire */}
                <div className="connexion-form-section">
                    <div className="form-container">
                        <div className="form-header">
                            <h2 className="form-title">Connexion</h2>
                            <p className="form-subtitle">
                                Entrez vos identifiants pour accéder à votre compte
                            </p>
                        </div>

                        {errors.general && (
                            <div className="error-message-container">
                                <FiAlertCircle className="error-icon" />
                                <span>{errors.general}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="connexion-form">
                            <div className="form-group">
                                <div className="input-group">
                                    <div className="input-icon">
                                        <FiMail />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={errors.email ? 'input-error' : ''}
                                        placeholder="Adresse email"
                                        disabled={loading}
                                    />
                                    <label className="floating-label">Email</label>
                                </div>
                                {errors.email && <span className="field-error">{errors.email}</span>}
                            </div>

                            <div className="form-group">
                                <div className="input-group">
                                    <div className="input-icon">
                                        <FiLock />
                                    </div>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className={errors.password ? 'input-error' : ''}
                                        placeholder="Mot de passe"
                                        disabled={loading}
                                    />
                                    <label className="floating-label">Mot de passe</label>
                                </div>
                                {errors.password && <span className="field-error">{errors.password}</span>}
                            </div>

                            <div className="form-options">
                                <label className="checkbox-container">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        disabled={loading}
                                    />
                                    <span className="checkmark"></span>
                                    <span className="checkbox-label">Se souvenir de moi</span>
                                </label>
                                <Link to="/mot-de-passe-oublie" className="forgot-password">
                                    Mot de passe oublié ?
                                </Link>
                            </div>

                            <button 
                                type="submit" 
                                className="submit-button"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="loading-spinnerDJJDJ">
                                        <div className="spinnerJDJD"></div>
                                        <span>Connexion en cours...</span>
                                    </div>
                                ) : (
                                    'Se connecter'
                                )}
                            </button>

                            <div className="divider">
                                <span className="divider-text">Ou continuer avec</span>
                            </div>

                            <div className="social-login">
                                <button type="button" className="social-button google">
                                    <FcGoogle className="social-icon" />
                                    <span>Google</span>
                                </button>
                                <button type="button" className="social-button facebook">
                                    <FaFacebook className="social-icon" />
                                    <span>Facebook</span>
                                </button>
                            </div>

                            <div className="form-footer">
                                <p className="signup-link">
                                    Pas encore de compte ?{' '}
                                    <Link to="/inscription" className="link">
                                        Créer un compte
                                    </Link>
                                </p>
                                <p className="terms">
                                    En vous connectant, vous acceptez nos{' '}
                                    <Link to="/conditions" className="link">Conditions d'utilisation</Link>{' '}
                                    et notre{' '}
                                    <Link to="/confidentialite" className="link">Politique de confidentialité</Link>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Connexion;